import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

import { ProductType } from '../types/api';
import { useForm } from 'react-hook-form';
import { useState, useEffect } from 'react';
import { currencyFormatter } from '../util';
import { calculateLineTotal } from '../utils/quote-totals';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (item: any) => void;
  skuRequired?: boolean;
  editingItem?: any;
}

export const CreateQuoteLineItemModal = (props: Props) => {
  const { skuRequired = true, editingItem } = props;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const initialFormData = {
    sku: '',
    type: '',
    description: '',
    unitPriceStr: '£0.00',
    unitPrice: 0,
    quantity: 1,
    taxRate: 2000, // 20% in basis points
  };

  const [formData, setFormData] = useState(initialFormData);

  // Populate form when editing an existing item
  useEffect(() => {
    if (editingItem) {
      // Determine product type from tax rate
      const type = editingItem.taxRate === 0 ? ProductType.BOOK : ProductType.INSTRUMENT;
      setFormData({
        sku: editingItem.sku || '',
        type: type,
        description: editingItem.description || '',
        unitPriceStr: currencyFormatter.format(editingItem.unitPrice / 100),
        unitPrice: editingItem.unitPrice || 0,
        quantity: editingItem.quantity || 1,
        taxRate: editingItem.taxRate ?? 2000,
      });
    } else {
      setFormData(initialFormData);
    }
  }, [editingItem]);

  const _onChange = (e: any) => {
    const k = e.target.id || e.target.name;
    const v = e.target.value;
    const d = { ...formData, [k]: v };

    // Set the correct tax rate if the type has changed
    if (k === 'type') {
      d.taxRate = v === ProductType.BOOK ? 0 : 2000; // Books are 0% VAT, others are 20%
    }

    if (k === 'quantity') {
      d.quantity = +v < 1 ? 1 : +v;
    }

    // Convert the string representation of the unit price into pence
    if (k === 'unitPriceStr') {
      d.unitPrice = +v.replace(/[^0-9]/g, '');
      d.unitPriceStr = currencyFormatter.format(d.unitPrice / 100);
    }

    setFormData({ ...formData, ...d });
  };

  const _save = () => {
    const item = {
      sku: formData.sku,
      description: formData.description,
      quantity: formData.quantity,
      unitPrice: formData.unitPrice,
      taxRate: formData.taxRate,
      lineTotal: calculateLineTotal(formData),
    };

    setFormData(initialFormData);
    props.onSave(item);
  };

  const _handleClose = () => {
    setFormData(initialFormData);
    props.onClose();
  };

  return (
    <Dialog open={props.open} onClose={_handleClose} fullWidth>
      <form
        onSubmit={handleSubmit(
          () => _save(),
          (e) => console.log(e)
        )}
      >
        <DialogTitle>{editingItem ? 'Edit Custom Item' : 'Add Custom Item'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ marginTop: 1 }}>
            {skuRequired && (
              <Grid size={6}>
                <FormControl fullWidth error={'sku' in errors}>
                  <TextField
                    required
                    id="sku"
                    label="SKU"
                    value={formData.sku}
                    onChange={_onChange}
                  />
                </FormControl>
              </Grid>
            )}
            <Grid size={skuRequired ? 6 : 12}>
              <FormControl fullWidth error={'type' in errors}>
                <InputLabel>Type *</InputLabel>
                <Select
                  {...register('type', { required: true })}
                  label="Type"
                  id="type"
                  value={formData.type}
                  required
                  sx={{ textAlign: 'left' }}
                  onChange={_onChange}
                >
                  {[
                    ProductType.BOOK,
                    ProductType.ACCESSORY,
                    ProductType.GIFT,
                    ProductType.INSTRUMENT,
                    ProductType.OTHER,
                  ].map((t) => (
                    <MenuItem key={t} id={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth error={'description' in errors}>
                <TextField
                  required
                  id="description"
                  label="Description"
                  value={formData.description}
                  onChange={_onChange}
                />
              </FormControl>
            </Grid>
            <Grid size={10}>
              <input
                type="number"
                hidden={true}
                id="unitPrice"
                value={formData.unitPrice}
                {...register('unitPrice', { min: 1 })}
              />
              <FormControl fullWidth error={'unitPrice' in errors}>
                <TextField
                  error={'unitPrice' in errors}
                  required
                  id="unitPriceStr"
                  label="Unit Price"
                  value={formData.unitPriceStr}
                  onChange={_onChange}
                />
                {'unitPrice' in errors && (
                  <FormHelperText>
                    Please enter a unit price, excluding any VAT
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={2}>
              <FormControl fullWidth>
                <TextField
                  required
                  id="quantity"
                  label="Quantity"
                  value={formData.quantity}
                  onChange={_onChange}
                  type="number"
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="secondary" onClick={_handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
